import {
  createContext,
  useContext,
  useState,
  useCallback,
  ReactNode,
  useRef,
} from 'react';

interface BookmarkExitAnimationContextValue {
  exitingBookmarkIds: Set<string>;
  markAsExiting: (ids: string[]) => void;
  onExitComplete: (id: string) => void;
}

const BookmarkExitAnimationContext = createContext<
  BookmarkExitAnimationContextValue | undefined
>(undefined);

export function useBookmarkExitAnimation() {
  const context = useContext(BookmarkExitAnimationContext);
  if (!context) {
    throw new Error(
      'useBookmarkExitAnimation must be used within BookmarkExitAnimationProvider',
    );
  }
  return context;
}

export function useOptionalBookmarkExitAnimation() {
  return useContext(BookmarkExitAnimationContext);
}

interface BookmarkExitAnimationProviderProps {
  children: ReactNode;
}

export function BookmarkExitAnimationProvider({
  children,
}: BookmarkExitAnimationProviderProps) {
  const [exitingBookmarkIds, setExitingBookmarkIds] = useState<Set<string>>(new Set());
  const pendingRemovals = useRef<Set<string>>(new Set());
  const removalTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  const markAsExiting = useCallback((ids: string[]) => {
    setExitingBookmarkIds((prev) => {
      const next = new Set(prev);
      ids.forEach((id) => next.add(id));
      return next;
    });
  }, []);

  const onExitComplete = useCallback((id: string) => {
    pendingRemovals.current.add(id);

    if (removalTimeout.current) {
      clearTimeout(removalTimeout.current);
    }

    removalTimeout.current = setTimeout(() => {
      setExitingBookmarkIds((prev) => {
        const next = new Set(prev);
        pendingRemovals.current.forEach((removedId) => next.delete(removedId));
        pendingRemovals.current.clear();
        return next;
      });
    }, 50);
  }, []);

  return (
    <BookmarkExitAnimationContext.Provider
      value={{
        exitingBookmarkIds,
        markAsExiting,
        onExitComplete,
      }}
    >
      {children}
    </BookmarkExitAnimationContext.Provider>
  );
}
